import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario10FallWithInjury, { scenario10FallWithInjuryAudioCaching, scenario10FallWithInjuryVideoSegments } from "../../../scenario10-fall-with-injury";
import { FallWithInjuryComponentsStep1ComponentPart1Config, FallWithInjuryComponentsStep1ComponentMainConfig } from "../fall-with-injury-components-step1";

export default class FallWithInjuryComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): FallWithInjuryComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: FallWithInjuryComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
			<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
			thing in similar circumstances.<br/><br/>
			Nurse #1: I would never put a call light out of the reach of the patient.<br/><br/>
			Nurse #2: Call lights are difficult to answer in our unit. There is no help 
			at the Nurses Station to answer the calls. Even so, it is basic safety protocol and ethics to ensure 
			that a patient can reach the call light if they need help. Even with short staffing and high task 
			loads, which seems so unfair to us. It’s not acceptable to put the call light out of reach.<br/><br/>
			Nurse #3: I have felt extremely anxious lately. My supervisor told us 
			that we will be evaluated on how quickly call lights are answered. Still, you must ensure the 
			patient at least has access to the call light. On our unit that doesn’t mean it will get answered 
			quickly. I think the patient would have fallen even if the call light was in reach.</p>
			`,
			okButton: 'Continue',
			talkingVideoSrc: scenario10FallWithInjuryVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario10FallWithInjuryVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			audioUrl: scenario10FallWithInjuryAudioCaching.getItemByKey(scenario10FallWithInjuryAudioCaching.audioKeys.question3).src,
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 1'),
			isYesCorrect: false,
			correctFeedback: `
				<p class="default-text">Correct. Although there is a cultural/systems issue on this unit 
				(short staffing, nurses not supported, staff feeling helpless and acknowledging that there 
					are long delays when answering call lights), none of the nurses said they would purposely 
					place the call light out of reach, all of the nurses agreed there was a safety and ethical 
					component to ensuring the patient had the ability to call for help.</p>
			`,
			wrongFeedback: `
				<p class="default-text">Not the best choice. Although there is a cultural/systems issue on 
				this unit (short staffing, nurses not supported, staff feeling helpless and acknowledging 
					that there are long delays when answering call lights), none of the nurses said they 
					would purposely place the call light out of reach, all of the nurses agreed there was a 
					safety and ethical component to ensuring the patient had the ability to call for help.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario10FallWithInjuryVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			audioUrl: scenario10FallWithInjuryAudioCaching.getItemByKey(scenario10FallWithInjuryAudioCaching.audioKeys.question4).src,
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 2'),
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. The substitution test demonstrates that no other employees would make a 
						similar choice and there are no system or cultural issues identified. Accountability for the employee’s 
						choice rests completely with them.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best choice. The substitution test demonstrates that no other 
						employees would make a similar choice and there are no system or cultural issues identified. Accountability 
						for the employee’s choice rests completely with them.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The substitution test demonstrates that there are 
						no system or cultural issues identified. Accountability for the employee’s choice rests completely with them.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				const mentorInfo = scenario10FallWithInjuryVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Is shared between the employee and organizational leadership.');
			}
		}, 3);
	}
}